import React from 'react';
import {
  ShieldAlert,
  ShieldCheck,
  AlertTriangle,
  Sparkles,
  Barcode,
  QrCode,
  Calendar,
  DollarSign,
  Type,
  Image as ImageIcon,
  CheckCircle2,
  XCircle,
  Hash,
  ArrowRight,
  Database
} from 'lucide-react';
import { CSVData, VdpTemplate } from '../../types';
import {
  ValidationReport,
  TemplateVariableValidation,
  ValidationIssue
} from '../../utils/csvValidation';

interface VisualDataValidationLayerProps {
  csvData: CSVData;
  template: VdpTemplate | null;
  validationReport: ValidationReport;
  onApplyFix: (issue: ValidationIssue) => void;
  onAutoCleanAll: () => void;
}

export const VisualDataValidationLayer: React.FC<VisualDataValidationLayerProps> = ({
  csvData,
  template,
  validationReport,
  onApplyFix,
  onAutoCleanAll
}) => {
  const { templateVariables, issues, healthScore, hasErrors, hasWarnings } = validationReport;

  const errorIssues = issues.filter(i => i.severity === 'error');
  const warningIssues = issues.filter(i => i.severity === 'warning');

  const missingVariables = templateVariables.filter(v => v.status === 'missing_column');
  const mismatchedVariables = templateVariables.filter(v => v.status === 'type_mismatch');
  const emptyVariables = templateVariables.filter(v => v.status === 'has_empty_cells');
  const validVariables = templateVariables.filter(v => v.status === 'valid');

  // Helper for type icons
  const renderTypeIcon = (type: string) => {
    switch (type) {
      case 'currency':
      case 'number':
        return <DollarSign className="w-4 h-4 text-emerald-400" />;
      case 'ean13':
      case 'upca':
      case 'barcode':
        return <Barcode className="w-4 h-4 text-cyan-400" />;
      case 'qr':
      case 'url':
        return <QrCode className="w-4 h-4 text-purple-400" />;
      case 'date':
        return <Calendar className="w-4 h-4 text-amber-400" />;
      case 'image':
        return <ImageIcon className="w-4 h-4 text-blue-400" />;
      default:
        return <Type className="w-4 h-4 text-slate-300" />;
    }
  };

  const handleFixVariable = (variable: TemplateVariableValidation) => {
    const issueMatch = issues.find(i => i.columnName === variable.variableName);
    if (issueMatch) {
      onApplyFix(issueMatch);
      return;
    }

    // Synthesize an issue if not found directly
    const syntheticIssue: ValidationIssue = {
      id: `fix_var_${variable.variableName}`,
      severity: variable.status === 'valid' ? 'info' : 'error',
      category: variable.status === 'missing_column' ? 'missing_columns' : 'type_mismatch',
      title: `Auto-Fix for ${variable.variableName}`,
      message: variable.statusMessage,
      columnName: variable.variableName,
      fixAction: variable.fixAction || (variable.status === 'missing_column' ? 'add_column' : 'sanitize_type'),
      fixLabel: variable.fixLabel || `Auto-Fix ${variable.variableName}`
    };

    onApplyFix(syntheticIssue);
  };

  return (
    <div id="visual-data-validation-layer" className="rounded-xl border border-slate-800 bg-slate-900/90 shadow-2xl p-5 mb-6">
      {/* Top Banner with Health & Auto-Fix All */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 pb-5 border-b border-slate-800">
        <div className="flex items-start gap-3.5">
          <div className={`p-2.5 rounded-xl flex items-center justify-center shrink-0 ${
            hasErrors 
              ? 'bg-red-950/80 border border-red-500 text-red-400 shadow-lg shadow-red-950/50' 
              : hasWarnings
              ? 'bg-amber-950/70 border border-amber-500 text-amber-400'
              : 'bg-emerald-950/70 border border-emerald-500 text-emerald-400'
          }`}>
            {hasErrors ? (
              <ShieldAlert className="w-6 h-6 animate-pulse" />
            ) : hasWarnings ? (
              <AlertTriangle className="w-6 h-6" />
            ) : (
              <ShieldCheck className="w-6 h-6" />
            )}
          </div>

          <div>
            <div className="flex flex-wrap items-center gap-2.5">
              <h3 className="text-base font-bold text-white tracking-wide flex items-center gap-2">
                Data Validation Layer
              </h3>
              <span className="text-xs px-2.5 py-0.5 rounded-full font-medium bg-slate-800 text-slate-300 border border-slate-700">
                Template: <span className="text-amber-400 font-semibold">{template?.name || 'Active Sticker'}</span>
              </span>

              {hasErrors ? (
                <span className="text-xs px-2.5 py-0.5 rounded-full font-semibold bg-red-950 text-red-300 border border-red-500/80 flex items-center gap-1.5 animate-pulse">
                  <XCircle className="w-3.5 h-3.5 text-red-400" />
                  {errorIssues.length} Error{errorIssues.length > 1 ? 's' : ''} Detected
                </span>
              ) : hasWarnings ? (
                <span className="text-xs px-2.5 py-0.5 rounded-full font-semibold bg-amber-950 text-amber-300 border border-amber-600 flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
                  {warningIssues.length} Warning{warningIssues.length > 1 ? 's' : ''}
                </span>
              ) : (
                <span className="text-xs px-2.5 py-0.5 rounded-full font-semibold bg-emerald-950 text-emerald-300 border border-emerald-600 flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                  Ready for Print Generation
                </span>
              )}
            </div>

            <p className="text-xs text-slate-400 mt-1">
              Scanned <span className="font-semibold text-slate-200">{csvData.rows.length} records</span> against <span className="font-semibold text-amber-300">{templateVariables.length} required template variables</span>.
              {hasErrors && ' Red highlighted fields require repair before printing.'}
            </p>
          </div>
        </div>

        {/* Health Score & Auto-Fix All Action */}
        <div className="flex items-center gap-3 shrink-0 self-end lg:self-center">
          <div className="hidden sm:flex flex-col items-end px-3 py-1.5 rounded-lg bg-slate-950/60 border border-slate-800 text-right">
            <span className="text-[10px] uppercase font-semibold text-slate-400 tracking-wider">Health Score</span>
            <span className={`text-base font-extrabold ${
              healthScore >= 90 ? 'text-emerald-400' : healthScore >= 70 ? 'text-amber-400' : 'text-red-400'
            }`}>
              {healthScore}%
            </span>
          </div>

          {(hasErrors || hasWarnings) && (
            <button
              id="btn-auto-fix-all-validation"
              type="button"
              onClick={onAutoCleanAll}
              className="px-4 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider text-slate-950 bg-gradient-to-r from-amber-400 via-amber-300 to-yellow-400 hover:from-amber-300 hover:to-yellow-300 shadow-lg shadow-amber-500/20 active:scale-95 transition-all flex items-center gap-2 cursor-pointer border border-amber-200"
            >
              <Sparkles className="w-4 h-4 text-slate-900 fill-slate-900" />
              <span>Auto-Fix All Issues</span>
            </button>
          )}
        </div>
      </div>

      {/* Required Template Variables Grid */}
      <div className="mt-5">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-300 flex items-center gap-2">
            <Database className="w-3.5 h-3.5 text-amber-400" />
            Active Template Required Variables ({templateVariables.length})
          </h4>
          <span className="text-[11px] text-slate-400">
            {missingVariables.length > 0 && <span className="text-red-400 font-bold mr-2">🔴 {missingVariables.length} Missing</span>}
            {mismatchedVariables.length > 0 && <span className="text-red-400 font-bold mr-2">🔴 {mismatchedVariables.length} Type Mismatch</span>}
            {emptyVariables.length > 0 && <span className="text-amber-400 font-bold mr-2">🟡 {emptyVariables.length} Empty</span>}
            <span className="text-emerald-400 font-medium">🟢 {validVariables.length} Valid</span>
          </span>
        </div>

        {templateVariables.length === 0 ? (
          <div className="p-4 rounded-lg bg-slate-950/40 border border-slate-800 text-xs text-slate-400">
            This template does not contain dynamic variable elements. All elements are static.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3.5">
            {templateVariables.map((variable) => {
              const isError = variable.status === 'missing_column' || variable.status === 'type_mismatch';
              const isWarning = variable.status === 'has_empty_cells';
              const isValid = variable.status === 'valid';

              return (
                <div
                  key={variable.variableName}
                  id={`var-card-${variable.variableName}`}
                  className={`relative p-3.5 rounded-xl transition-all border ${
                    isError
                      ? 'bg-red-950/30 border-red-500/80 shadow-md shadow-red-950/30 ring-1 ring-red-500/30'
                      : isWarning
                      ? 'bg-amber-950/20 border-amber-600/70'
                      : 'bg-slate-950/40 border-slate-800/90 hover:border-slate-700'
                  }`}
                >
                  {/* Card Header */}
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 rounded-lg bg-slate-800 border border-slate-700 shrink-0">
                        {renderTypeIcon(variable.elementType)}
                      </div>
                      <div>
                        <div className="flex items-center gap-1.5">
                          <span className="font-mono text-xs font-bold text-white">
                            &#123;&#123;{variable.variableName}&#125;&#125;
                          </span>
                        </div>
                        <span className="text-[10px] text-slate-400 uppercase tracking-wider block">
                          Type: {variable.expectedDataType}
                        </span>
                      </div>
                    </div>

                    {/* Status Badge */}
                    <div className="shrink-0">
                      {variable.status === 'missing_column' && (
                        <span className="text-[10px] px-2 py-0.5 rounded-md font-bold bg-red-950 text-red-300 border border-red-500/80 inline-flex items-center gap-1">
                          <XCircle className="w-3 h-3 text-red-400" />
                          Missing Column
                        </span>
                      )}
                      {variable.status === 'type_mismatch' && (
                        <span className="text-[10px] px-2 py-0.5 rounded-md font-bold bg-red-950 text-red-300 border border-red-500/80 inline-flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3 text-red-400" />
                          Type Mismatch
                        </span>
                      )}
                      {variable.status === 'has_empty_cells' && (
                        <span className="text-[10px] px-2 py-0.5 rounded-md font-bold bg-amber-950 text-amber-300 border border-amber-600 inline-flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3 text-amber-400" />
                          Empty Cells
                        </span>
                      )}
                      {variable.status === 'valid' && (
                        <span className="text-[10px] px-2 py-0.5 rounded-md font-medium bg-emerald-950/80 text-emerald-300 border border-emerald-700/80 inline-flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                          Valid
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Card Message / Error Details */}
                  <div className="text-xs mt-2 min-h-[38px] flex flex-col justify-center">
                    {isError ? (
                      <p className="text-red-300 text-[11px] leading-relaxed font-medium">
                        {variable.status === 'missing_column'
                          ? `Column is missing in CSV. Add this column or map an existing one.`
                          : `${variable.invalidRowIndices.length} record(s) fail ${variable.expectedDataType.toUpperCase()} validation (e.g., "${variable.invalidSampleValues.slice(0, 2).join(', ')}").`}
                      </p>
                    ) : isWarning ? (
                      <p className="text-amber-300 text-[11px] leading-relaxed">
                        {variable.statusMessage}
                      </p>
                    ) : (
                      <p className="text-slate-400 text-[11px] truncate">
                        Sample: <span className="text-slate-200 font-mono">"{variable.validSampleValue || '—'}"</span>
                      </p>
                    )}
                  </div>

                  {/* Card Action Footer */}
                  {!isValid && (
                    <div className="mt-3 pt-2.5 border-t border-slate-800/80 flex items-center justify-between">
                      <span className="text-[10px] text-slate-400 italic">
                        {variable.suggestedFix ? 'Auto-repair ready' : 'Action needed'}
                      </span>
                      <button
                        id={`btn-fix-${variable.variableName}`}
                        type="button"
                        onClick={() => handleFixVariable(variable)}
                        className={`text-xs px-2.5 py-1 rounded-lg font-bold flex items-center gap-1.5 transition-all cursor-pointer ${
                          isError
                            ? 'bg-red-500 hover:bg-red-400 text-white shadow-sm shadow-red-900/40 active:scale-95'
                            : 'bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold active:scale-95'
                        }`}
                      >
                        <Sparkles className="w-3 h-3" />
                        <span>{variable.fixLabel || 'Auto-Fix'}</span>
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
